import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authApi } from "../api/services";
import { AUTH_CLEARED_EVENT, getErrorMessage, tokenStore } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [loading, setLoading] = useState(
    Boolean(tokenStore.getAccess() || tokenStore.getRefresh())
  );

  const applySession = useCallback((data) => {
    tokenStore.setSession(data);
    setUser(data.user);
  }, []);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    const onCleared = () => setUser(null);
    window.addEventListener(AUTH_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, onCleared);
  }, []);

  useEffect(() => {
    const access = tokenStore.getAccess();
    const refresh = tokenStore.getRefresh();

    if (!access && !refresh) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    authApi
      .me()
      .then((res) => {
        if (cancelled) return;
        const nextUser = res.data.data.user;
        tokenStore.setSession({ user: nextUser });
        setUser(nextUser);
      })
      .catch((error) => {
        if (cancelled) return;
        // Keep the cached session on network/server errors.
        // Only clear when auth is definitively rejected (and refresh already failed).
        if (error.response?.status === 401) {
          clearSession();
        } else if (!tokenStore.getUser()) {
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(
    async (payload) => {
      const res = await authApi.login(payload);
      applySession(res.data.data);
      return res.data.data.user;
    },
    [applySession]
  );

  const register = useCallback(async (payload) => {
    await authApi.register(payload);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefresh();
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {
      // ignore logout API errors
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      register,
      logout,
      getErrorMessage,
    }),
    [user, loading, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
